import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'app/store';
import { ApiResListsType } from 'global-services/api/OhsApiModels';
import { getOhsLocalStorage, setOhsLocalStorage } from 'global-services/OhsDataParse';
import { OhsActionModuleViewPresets } from 'dashboard/topFilter/OhsAssetFilterModels';
import { OhsUser } from 'user/OhsUser';
import OhsUserLocalServices from 'user/OhsUserLocalServices';

import { getActionLists, getActionListsWithTasks, getActionSearchLists } from './OhsActionServices';
import { OhsActionFilterPayload, OhsActionSearchMain, OhsActionsRecord } from './OhsActionModels';

export interface ActionListsRecordState {
  isLoading: boolean;
  actionLists: ApiResListsType<any[]> | null;
  actionTasksLists: ApiResListsType<any[]> | null;
  currentPage: number;
  currentViewPreset: OhsActionModuleViewPresets;
  showSignoffActions: boolean;
}

const initialState: ActionListsRecordState = {
  isLoading: false,
  actionLists: null,
  actionTasksLists: null,
  currentPage: 1,
  currentViewPreset: OhsActionModuleViewPresets.view_1,
  showSignoffActions: false,
};

export const getActionViewPreset = (tierNum: number) => {
  switch (tierNum) {
    case 3:
      return OhsActionModuleViewPresets.view_1;
    case 4:
    case 5:
      return OhsActionModuleViewPresets.view_2;
    default:
      return OhsActionModuleViewPresets.view_1;
  }
};

const actionListsRPC = async (
  payload: OhsActionFilterPayload | OhsActionSearchMain,
  hasSearch: boolean = false
): Promise<any> => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;

  if (
    userTier &&
    (userTier === 2 || userTier === 3 || userTier === 4) &&
    user?.configs.action?.PERMISSIONS.view
  ) {
    const response = await getActionListsWithTasks(payload, hasSearch);
    return response;
  }
  if (userTier && userTier === 5 && user?.configs.action?.PERMISSIONS.view) {
    const response = hasSearch
      ? await getActionSearchLists(payload as OhsActionSearchMain)
      : await getActionLists(payload as OhsActionFilterPayload);
    return response;
  }
  return null;
};

export const fetchActionListAsync = createAsyncThunk<
  {
    list: ApiResListsType<OhsActionsRecord[]> | null;
    count: ApiResListsType<OhsActionsRecord[]> | null;
  },
  {
    list: OhsActionFilterPayload | OhsActionSearchMain;
    count: OhsActionFilterPayload | OhsActionSearchMain;
    hasSearch: boolean;
  },
  { state: RootState }
>('actions/fetchActionListAsync', async (payload, thunkAPI) => {
  try {
    const [list, count] = await Promise.all([
      payload.hasSearch
        ? actionListsRPC(payload.list as OhsActionSearchMain, true)
        : actionListsRPC(payload.list as OhsActionFilterPayload),
      payload.hasSearch
        ? actionListsRPC(payload.count as OhsActionSearchMain, true)
        : getActionLists(payload.count as OhsActionFilterPayload),
    ]);

    return { list, count };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const actionSlice = createSlice({
  name: 'actions',
  initialState,
  reducers: {
    setActionCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setActionIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setActionViewPreset: (state, action: PayloadAction<OhsActionModuleViewPresets>) => {
      state.currentViewPreset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActionListAsync.pending, (state) => {
        state.isLoading = true;
        state.actionLists = {
          items: [],
          pagination: state.actionLists?.pagination ?? {
            page: 0,
            totalPages: 0,
            next: '',
          },
        };
      })
      .addCase(fetchActionListAsync.fulfilled, (state, action) => {
        if (action.payload.list) {
          state.actionLists = action.payload.list;
          if (state?.actionLists?.pagination?.totalPages) {
            setOhsLocalStorage('actionListTotalPages', state.actionLists.pagination.totalPages);
          }
          let setItemsLastId = '';
          const actionPlanItems = [...state.actionLists.items];
          if (actionPlanItems.length > 0) setItemsLastId = actionPlanItems.pop()._id;

          state.actionLists.pagination.next = setItemsLastId;
          state.actionLists.pagination.totalPages = getOhsLocalStorage('actionListTotalPages') ?? 0;
          state.isLoading = false;
        }
        if (action.payload.count && state.actionLists) {
          setOhsLocalStorage('actionListTotalPages', action.payload.count.pagination.totalPages);
          state.actionLists.pagination.totalPages = action.payload.count.pagination.totalPages ?? 0;
        }
      })
      .addCase(fetchActionListAsync.rejected, (state) => {
        state.actionLists = null;
      });
  },
});

const actionsState = (state: RootState) => state.actionsReport;
export const getOhsActionsLists = createSelector([actionsState], (actionsReport) => actionsReport);
export const { setActionCurrentPage, setActionIsLoading, setActionViewPreset } =
  actionSlice.actions;
export const actionModuleReducer = actionSlice.reducer;
